// tslint:disable:no-commented-code
// tslint:disable:no-duplicate-string
// tslint:disable:only-arrow-functions

import { ProductReferenceDto } from '@epicuro-next/data/engagement/engagement-models';
import {
  IDynamicBindableValue,
  IDynamicInput,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';
import { FilterQueryParams } from '@epicuro-next/models/products';
import { ExtendedFilterQueryParams } from '@epicuro-next/state/p-basket-state';
import { transformFilterQueryParamToSearchQuery } from '@epicuro-next/utilities';
import { SearchQuery } from 'graphql-api/gql-api-interfaces';

import {
  BrandOrgType,
  getBrandOrganisation,
} from './tenants/common/bought-this-season/brand-org';

export function getProductDataFromProductReferencesProvider(providers: {
  productReferences: IDynamicBindableValue;
}): IDynamicBindableValue {
  const provider = {
    provider: 'ProductBasketProvider',
    property: {
      values: [providers.productReferences, getBrandOrganisation()],
      transform: function ([productReferences, brandOrg]: [
        ProductReferenceDto[],
        BrandOrgType,
      ]): SearchQuery {
        const brandId = brandOrg.brandId;
        const searchQuery: SearchQuery = {};
        searchQuery.useV2Search = true;
        searchQuery.collapse = false;
        searchQuery.withRelatedVariants = true;
        searchQuery.productReferences = productReferences.map((pr) => ({
          productId: pr.productId,
          brandId,
        }));
        searchQuery.available = true;
        searchQuery.num = Math.min(productReferences.length, 100);
        return searchQuery;
      }.toString(),
    },
  };

  return provider;
}

export function getPBasketSearchResultsStringProvider(providers: {
  url: IDynamicInput;
  nrOfProducts: IDynamicInput;
}) {
  const provider = {
    provider: 'ProductBasketProvider',
    property: {
      values: [providers.url, providers.nrOfProducts],
      transform: function ([searchUrl, nrOfProducts]: [
        string,
        string,
      ]): SearchQuery | null {
        const searchQuery =
          window.neoclesDynamicFramework.recommendationsPanels.methods.parseSearchUrlToQuery(
            searchUrl,
            nrOfProducts,
          );
        return searchQuery;
      }.toString(),
    },
  };

  return provider;
}

export function parseSearchUrlToSearchQuery(
  searchUrl: string,
  nrOfProducts?: string,
): SearchQuery | null {
  type NonStringFilterQueryParamsType = {
    [K in keyof ExtendedFilterQueryParams as ExtendedFilterQueryParams[K] extends
      | string
      | null
      | undefined
      ? never
      : K]: Exclude<ExtendedFilterQueryParams[K], null | undefined>;
  };

  type MappingStringToTypeType = Required<{
    [K in keyof NonStringFilterQueryParamsType]: (
      input: string,
    ) => Exclude<NonStringFilterQueryParamsType[K], undefined>;
  }>;

  type UnionOfPropertyTypes = Exclude<
    ExtendedFilterQueryParams[keyof ExtendedFilterQueryParams],
    null | undefined
  >;

  const stringToBoolean = (input: string) =>
    input.trim().toLowerCase() === 'true';

  const typeMapping: MappingStringToTypeType = {
    collapse: stringToBoolean,
    withRelatedVariants: stringToBoolean,
    useV2Search: stringToBoolean,
    useCatalogOnlyMode: stringToBoolean,
    start: (input: string) => Number(input),
    num: (input: string) => Number(input),
  };

  const indexOfSearch = searchUrl.indexOf('search');
  if (indexOfSearch === -1) {
    return null;
  }

  const queryParamsString = searchUrl.substring(indexOfSearch);
  // new URL wants a full URL including host name, but it is not guaranteed user specifies it.
  let fullUrl = `https://dummy-domain.com/${queryParamsString}`;
  if (nrOfProducts) {
    fullUrl = fullUrl + `&num=${nrOfProducts}`;
  }
  const url = new URL(fullUrl);

  const filterQueryParam: { [key: string]: UnionOfPropertyTypes } = {
    withRelatedVariants: true,
    useV2Search: true,
  };

  for (const [key, value] of url.searchParams.entries()) {
    let v: UnionOfPropertyTypes = value;
    if (key in typeMapping) {
      const mappingFunction = typeMapping[key as keyof MappingStringToTypeType];
      v = mappingFunction(value);
    }
    filterQueryParam[key] = v;
  }

  const searchQuery = transformFilterQueryParamToSearchQuery(
    filterQueryParam as FilterQueryParams,
  );

  return searchQuery;
}
